import { useNavigate } from '@solidjs/router';
import { createEffect, createSignal, For, type JSX, on, Show } from 'solid-js';
import { t } from '../../i18n';
import { orgHref, urlOrg } from '../../lib/org';
import { commandHelpLines } from '../../lib/terminal/command-help';
import { type CommandIntent, parseCommand } from '../../lib/terminal/commands';
import { createTerminalKeymap } from '../../lib/terminal/keymap';
import { useCommandHistory, useCompletion } from '../../lib/terminal/use-command-line';

// The Terminal command line's UI half (#4398, epic #4391): the one visible
// input, its history, and the wiring seam over `parseCommand` — the pure
// parser (`lib/terminal/commands.ts`) owns the grammar and the closed,
// no-exec intent union; this component owns focus, history and printing, and
// is the ONLY place a `CommandIntent` turns into an effect.
//
// ALWAYS MOUNTED, ALWAYS VISIBLE — never a toggled overlay. `:` (the global
// focus binding, `lib/terminal/keymap.ts`) and the pane title-bar's trigger
// button (`TerminalPaneTitlebar.tsx`) both just move FOCUS to this one
// already-rendered input, mirroring how `/` in `CustomPlansSection.tsx`
// focuses its always-visible search box rather than summoning one. That is
// also why `createTerminalKeymap` can safely live in THIS component's own
// `onMount`: the global `:` listener needs to exist even before anyone has
// used the command line, and it always does.
//
// SPLIT OF RESPONSIBILITY AT THE WIRING SEAM. This component knows how to
// navigate (a cross-cutting, non-pane-specific capability — `useNavigate` +
// `orgHref`, the same pattern `CommandPaletteDialog.tsx` uses) and how to
// print its own feedback. It does NOT know about panes, layout or focus ids —
// `split`/`zoom`/`close`/`filter` intents are handed to the caller via
// `onIntent`, which is what lets `Terminal.tsx` target the focused pane
// itself, read fresh at dispatch time. Mounted ONCE, never per focused pane: a
// remount on a focus switch dropped a half-typed command (plan 101 slice 19,
// suspected 19-S3).
//
// PRINT IS NOT A TERMINAL ROW. A `print` intent (a usage message, "command not
// found", the `open pr` refusal) renders as this component's OWN transient
// feedback line — never a `sys:`-prefixed row minted into the shared ring
// buffer. Minting one would make every OTHER pane's `rows().length` grow for
// a message that only ever meant something to whoever typed the command.
//
// A `print` intent carries an `en.json` KEY (`commands.ts`'s
// `CommandMessageKey`) and its params, rendered here through `t()` — the
// parser stays i18n-free by naming the message, never spelling it.
//
// `help`, `history` and `status` ANSWER rather than act: they print several
// feedback lines here (`help` from `lib/terminal/command-help.ts`, `history`
// off this component's own list, `status` from the caller's `describeStatus`,
// which owns the pane/stream state). The prompt glyph (`<org>@developerz-ai:~$`)
// is decorative and `aria-hidden`; the input's accessible name stays the
// visually-hidden label.
export type CommandLinePaneIntent = Extract<
  CommandIntent,
  { kind: 'split' | 'zoom' | 'close' | 'filter' | 'tail' | 'follow' | 'pause' | 'clear' }
>;

export function CommandLine(props: {
  /**
   * Which pane a `split`/`zoom`/`close`/`filter` intent should target — the
   * caller's business, not this component's: `onIntent` fires with the
   * PARSED intent alone, and `Terminal.tsx` reads the focused pane id fresh
   * at dispatch time. Nothing here needs to know a pane id exists.
   */
  readonly onIntent: (intent: CommandLinePaneIntent) => void;
  /** Bumped by the pane title-bar's trigger button to move focus here. */
  readonly focusRequest: () => number;
  /** The `status` verb's one line — the caller holds the stream and pane state. */
  readonly describeStatus: () => string;
  /** The next/previous-pane chords (Alt+Shift+N/P) — the caller owns the layout. */
  readonly onCyclePane: (direction: 1 | -1) => void;
  /** Ctrl/⌘+F (#4401) — the caller opens the focused pane's search. */
  readonly onSearch: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  let inputRef: HTMLInputElement | undefined;

  const [value, setValue] = createSignal('');
  const [feedback, setFeedback] = createSignal<readonly string[] | null>(null);
  const history = useCommandHistory(value, setValue);
  const complete = useCompletion(value, setValue);

  function submit(): void {
    const line = value().trim();
    if (line === '') return; // Enter on an empty prompt does nothing — nothing to run.
    const lines = history.record(line);
    setValue('');
    run(parseCommand(line), lines);
  }

  /** The one place a parsed intent turns into an effect. */
  function run(intent: CommandIntent, lines: readonly string[]): void {
    switch (intent.kind) {
      case 'navigate':
        setFeedback(null);
        navigate(orgHref(intent.path));
        return;
      case 'print':
        setFeedback([intent.params === undefined ? t(intent.key) : t(intent.key, intent.params)]);
        return;
      case 'help':
        setFeedback(commandHelpLines());
        return;
      case 'history':
        setFeedback(lines.map((l, i) => `${String(i + 1).padStart(4)}  ${l}`));
        return;
      case 'status':
        setFeedback([props.describeStatus()]);
        return;
      default:
        setFeedback(null);
        props.onIntent(intent);
    }
  }

  createTerminalKeymap(
    {
      onFocusRepl: () => inputRef?.focus(),
      onHistoryPrev: history.prev,
      onHistoryNext: history.next,
      onSubmit: submit,
      onBlur: () => {
        history.leave();
        inputRef?.blur();
      },
      onComplete: complete,
      onSplitRow: () => props.onIntent({ kind: 'split', axis: 'row' }),
      onSplitColumn: () => props.onIntent({ kind: 'split', axis: 'column' }),
      onToggleZoom: () => props.onIntent({ kind: 'zoom' }),
      onClosePane: () => props.onIntent({ kind: 'close' }),
      onCyclePane: (direction) => props.onCyclePane(direction),
      onSearch: () => props.onSearch(),
    },
    () => inputRef,
  );

  // Skips the initial mount (`defer: true`) — only a genuine bump (the
  // title-bar button) should steal focus, never the first render.
  createEffect(
    on(
      () => props.focusRequest(),
      () => inputRef?.focus(),
      { defer: true },
    ),
  );

  const prompt = (): string => {
    const org = urlOrg();
    return org === null
      ? t('terminal.command_prompt_no_org')
      : t('terminal.command_prompt', { org });
  };

  return (
    <div class="terminal__command-line">
      <label class="sr-only" for="terminal-command-line-input">
        {t('terminal.command_line_label')}
      </label>
      <div class="terminal__command-line-row">
        <span class="terminal__command-line-prompt" aria-hidden="true">
          {prompt()}
        </span>
        <input
          id="terminal-command-line-input"
          ref={inputRef}
          type="text"
          class="terminal__command-line-input"
          placeholder={t('terminal.command_line_placeholder')}
          autocomplete="off"
          spellcheck={false}
          value={value()}
          onInput={(e) => setValue(e.currentTarget.value)}
        />
      </div>
      <Show when={feedback()}>
        {(lines) => (
          <div class="terminal__command-line-feedback" role="status">
            <For each={lines()}>{(line) => <p>{line}</p>}</For>
          </div>
        )}
      </Show>
    </div>
  );
}
